import { computed, readonly, ref } from 'vue'
import type { AuthSession } from '@/api/types'
import type { PortalPushSubscriptionCommand } from '@/api/portalMessaging'
import { portalPushApi, setNotificationContext } from '@/api/portalPush'
import { isPortalPreviewActive, PortalPreviewReadonlyError } from './portalPreview'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}
const installPrompt = ref<BeforeInstallPromptEvent | null>(null)
const installed = ref(false)
const online = ref(typeof navigator === 'undefined' ? true : navigator.onLine)
const workerError = ref('')
const registration = ref<ServiceWorkerRegistration | null>(null)
const updateAvailable = ref(false)
const permission = ref<NotificationPermission | 'unsupported'>('unsupported')
const canInstall = computed(() => installPrompt.value !== null && !installed.value)
const ios = typeof navigator !== 'undefined' && (/iPad|iPhone|iPod/.test(navigator.userAgent) || navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
let initialized = false
let activeAccount: string | null = null
let generation = 0
let registering: Promise<ServiceWorkerRegistration> | null = null
let adminContext = false

function standalone(): boolean {
  return typeof window !== 'undefined' && (window.matchMedia('(display-mode: standalone)').matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true)
}
export function pushSupported(): boolean {
  return typeof window !== 'undefined' && window.isSecureContext && 'serviceWorker' in navigator
    && 'PushManager' in window && 'Notification' in window
}
export function notificationPermission(): NotificationPermission | 'unsupported' {
  return pushSupported() ? Notification.permission : 'unsupported'
}
function refreshPermission(): void { permission.value = notificationPermission(); installed.value = standalone() }

export function initializePortalPwa(admin = false): void {
  if (isPortalPreviewActive()) return
  if (initialized || typeof window === 'undefined') return
  initialized = true
  adminContext = admin
  setNotificationContext(admin)
  refreshPermission()
  window.addEventListener('online', () => {
    online.value = true
    if (activeAccount) void portalPushApi.configuration().then(config => syncPortalPushAccount(config.accountId ?? activeAccount)).catch(() => undefined)
  })
  window.addEventListener('offline', () => { online.value = false })
  window.addEventListener('focus', refreshPermission)
  document.addEventListener('visibilitychange', refreshPermission)
  window.addEventListener('beforeinstallprompt', event => {
    event.preventDefault(); installPrompt.value = event as BeforeInstallPromptEvent
  })
  window.addEventListener('appinstalled', () => { installed.value = true; installPrompt.value = null })
  window.addEventListener('avoschool:explicit-logout', () => { void disablePortalPush().catch(() => undefined) })
  window.addEventListener('avoschool:session-changed', event => {
    const session = (event as CustomEvent<AuthSession | null>).detail
    void syncPortalPushAccount(session?.accountId ?? null)
  })
  if ('serviceWorker' in navigator && window.isSecureContext) {
    registering = navigator.serviceWorker.register('/portal-sw.js', { scope: '/' }).then(value => {
      registration.value = value
      updateAvailable.value = Boolean(value.waiting)
      value.addEventListener('updatefound', () => {
        value.installing?.addEventListener('statechange', () => { updateAvailable.value = Boolean(value.waiting && navigator.serviceWorker.controller) })
      })
      return value
    }).catch(error => { workerError.value = 'Не удалось подготовить приложение. Проверьте соединение и обновите страницу.'; throw error })
    void registering.catch(() => undefined)
    navigator.serviceWorker.addEventListener('controllerchange', () => { void publishAudience(activeAccount) })
  }
}

export const portalPwaState = {
  canInstall: readonly(canInstall), installed: readonly(installed), online: readonly(online),
  permission: readonly(permission), workerError: readonly(workerError), updateAvailable: readonly(updateAvailable), ios,
}
function timeout<T>(promise: Promise<T>, milliseconds = 5000): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error('Приложение пока не готово. Обновите страницу и повторите.')), milliseconds)
    promise.then(value => { clearTimeout(timer); resolve(value) }, error => { clearTimeout(timer); reject(error) })
  })
}
async function readyWorker(): Promise<ServiceWorkerRegistration> {
  if (!('serviceWorker' in navigator) || !window.isSecureContext) throw new Error('Для уведомлений откройте приложение по защищённому адресу HTTPS.')
  if (registering) await timeout(registering)
  return timeout(navigator.serviceWorker.ready)
}
async function publishAudience(accountId: string | null): Promise<void> {
  if (!('serviceWorker' in navigator)) return
  // Send immediately to the controlling worker even if a replacement worker failed to register.
  navigator.serviceWorker.controller?.postMessage({ type: 'SET_ACCOUNT', accountId })
  try {
    const worker = await readyWorker()
    worker.active?.postMessage({ type: 'SET_ACCOUNT', accountId: activeAccount })
  } catch { /* The settings screen exposes registration failures. */ }
}
export async function syncPortalPushAccount(accountId: string | null): Promise<void> {
  if (isPortalPreviewActive()) return
  const version = ++generation
  activeAccount = accountId
  await publishAudience(accountId)
  if (!pushSupported()) return
  try {
    const subscription = await existingPushSubscription()
    if (version !== generation) return
    if (!accountId) return
    if (subscription && online.value) {
      const [config, devices] = await Promise.all([portalPushApi.configuration(), portalPushApi.devices()])
      // Browser permission survives remote revocation and account changes. It is not this
      // account's consent to re-register an absent device; only the explicit enable action is.
      if (version !== generation || !config.configured || config.accountId !== accountId || !devices.some(device => device.current)) return
      await portalPushApi.register(subscription, deviceName(), true)
    }
  } catch { /* A failed sync never restores the previous notification audience. */ }
}
export function deviceName(): string {
  if (ios) return 'iPhone / iPad'
  if (/Android/.test(navigator.userAgent)) return 'Android'
  return /Mac/.test(navigator.platform) ? 'Mac' : 'Компьютер'
}
export function portalNotificationAdminContext(): boolean { return adminContext }
export async function installPortal(): Promise<boolean> {
  const prompt = installPrompt.value
  if (!prompt) return false
  await prompt.prompt()
  const choice = await prompt.userChoice
  if (choice.outcome === 'accepted') installPrompt.value = null
  return choice.outcome === 'accepted'
}
export async function updatePortal(): Promise<void> {
  const worker = await readyWorker()
  if (!worker.waiting) return
  navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), { once: true })
  worker.waiting.postMessage({ type: 'ACTIVATE_UPDATE' })
}
export async function setPortalBadge(count: number): Promise<void> {
  if (isPortalPreviewActive()) return
  const api = navigator as Navigator & { setAppBadge?: (value: number) => Promise<void>; clearAppBadge?: () => Promise<void> }
  try { if (count > 0) await api.setAppBadge?.(count); else await api.clearAppBadge?.() } catch { /* Optional OS capability. */ }
}
function urlBase64ToUint8Array(value: string): Uint8Array<ArrayBuffer> {
  const raw = window.atob((value + '='.repeat((4 - value.length % 4) % 4)).replace(/-/g, '+').replace(/_/g, '/'))
  return Uint8Array.from(raw, char => char.charCodeAt(0))
}
function bytesToBase64Url(value: ArrayBuffer | null): string {
  return value ? window.btoa(String.fromCharCode(...new Uint8Array(value))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '') : ''
}
function subscriptionCommand(subscription: PushSubscription): PortalPushSubscriptionCommand {
  return { endpoint: subscription.endpoint, p256dh: bytesToBase64Url(subscription.getKey('p256dh')), auth: bytesToBase64Url(subscription.getKey('auth')) }
}
export async function existingPushSubscription(): Promise<PortalPushSubscriptionCommand | null> {
  if (!pushSupported()) return null
  const subscription = await (await readyWorker()).pushManager.getSubscription()
  return subscription ? subscriptionCommand(subscription) : null
}
export async function enablePortalPush(publicKey: string): Promise<PortalPushSubscriptionCommand> {
  if (isPortalPreviewActive()) throw new PortalPreviewReadonlyError()
  if (!pushSupported()) throw new Error(ios && !installed.value ? 'Сначала добавьте Avoschool на экран «Домой» и откройте его оттуда.' : 'Этот браузер не поддерживает уведомления.')
  // Must precede awaits that would consume the user gesture on iOS.
  const result = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission()
  refreshPermission()
  if (result !== 'granted') throw new Error('Разрешите уведомления для Avoschool в настройках устройства или браузера.')
  const worker = await readyWorker()
  const existing = await worker.pushManager.getSubscription()
  const subscription = existing ?? await worker.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) })
  await publishAudience(activeAccount)
  return subscriptionCommand(subscription)
}
export async function disablePortalPush(): Promise<string | null> {
  if (isPortalPreviewActive()) return null
  if (!pushSupported()) return null
  const worker = await readyWorker()
  const subscription = await worker.pushManager.getSubscription()
  if (!subscription) return null
  await subscription.unsubscribe()
  return subscription.endpoint
}
